import { Configuration, PublicClientApplication, LogLevel } from '@azure/msal-browser';

/**
 * Microsoft Entra ID (MSAL) configuration.
 *
 * These were hardcoded, and pointed at an app registration belonging to an
 * unrelated Dynamics app that exposed no API of its own. BIKI now has its own
 * registration, and the values come from the environment so dev and production
 * can differ without a code change.
 */

const clientId = (import.meta.env.VITE_MSALE_CLIENT_ID ?? '').trim();
const authority = (import.meta.env.VITE_MSALE_AUTHORITY ?? '').trim();

/**
 * Scope for BIKI's own API, e.g. `api://<client-id>/access_as_user`.
 *
 * This is what makes the token usable for authorisation. The app previously
 * requested `User.Read`, which yields a token audienced to Microsoft Graph -
 * the API could never have validated it, whatever it did.
 */
export const apiScope = (import.meta.env.VITE_API_SCOPE ?? '').trim();

if (!clientId || !authority) {
    // Failing loudly here beats an opaque MSAL error on first sign-in.
    throw new Error(
        'VITE_MSALE_CLIENT_ID and VITE_MSALE_AUTHORITY must be set. ' +
        'Copy frontend/.env.example to .env.local for local development.'
    );
}
if (!apiScope) {
    throw new Error(
        'VITE_API_SCOPE must be set to BIKI\'s API scope (api://<client-id>/access_as_user). ' +
        'Without it the app requests no API token and every call is rejected.'
    );
}

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

/**
 * Sign-in request. Asks for the API scope up front so the first silent token
 * acquisition after login succeeds without a second interactive prompt, and
 * for `User.Read` because the profile avatar is fetched from Microsoft Graph -
 * consenting to both at sign-in avoids a second prompt later.
 */
export const loginRequest = { scopes: [apiScope, 'User.Read'] };

/** Token request used for every API call. */
export const tokenRequest = { scopes: [apiScope] };

export const msalInstance = new PublicClientApplication(msalConfig);

export const logout = () => {
  const isInIframe = window.self !== window.top;
  if (isInIframe) {
    msalInstance.logoutPopup({
      postLogoutRedirectUri: '/',
    });
  } else {
    msalInstance.logoutRedirect({
      postLogoutRedirectUri: '/',
    });
  }
};
